import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import { getDetailsForUser } from "../../services/UserService";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import Icons from "../../utils/components/icons";
import { formatDate } from "../../utils/utils";
import { UserContext } from "../MainLayout";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";
import { updateCommonInfo } from "../../utils/constants/userConstants";
import { browserName, isMobile, osName } from "react-device-detect";

function LogoutOptions() {
  const { t } = useTranslation("header");
  const { userCognitoData } = useContext(UserContext);
  const [isLoading, setLoading] = useState(true);

  const currentDeviceKey = useMemo(() => {
    return userCognitoData?.signInUserSession?.accessToken?.payload?.device_key;
  }, [userCognitoData]);

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    msg: "",
    type: "",
    data: {},
  });

  const [showDetails, setShowDetails] = useState(null);
  const [otherDeviceDetails, setOtherDeviceDetails] = useState([]);
  const [currentDevicesDetails, setCurrentDevicesDetails] = useState(null);
  const [deviceCommonInfoObj, setDeviceCommonInfoObj] = useState();
  const [err, setErr] = useState(false);

  /** Sequence-2: getUserProfileDetails to get device details and set states **/
  const getUserProfileDetails = useCallback(async () => {
    setErr(false); 
    try {
      const queryParam = {
        cognitoId:
          userCognitoData?.signInUserSession?.accessToken?.payload?.username,
      };
      const response = await getDetailsForUser(queryParam);
      const deviceLoginCommonInfoObj =
        response?.data?.UserDetails?.ClientData?.commonInfo;
      setDeviceCommonInfoObj(deviceLoginCommonInfoObj);
      if (deviceLoginCommonInfoObj) {
        const devicesDetails = [];
        for (const key in deviceLoginCommonInfoObj) {
          const deviceKey = key.split("&")[0];
          deviceLoginCommonInfoObj[key].DeviceID = deviceKey;
          if (deviceKey === currentDeviceKey) {
            setCurrentDevicesDetails(deviceLoginCommonInfoObj[key]);
          } else {
            if (deviceLoginCommonInfoObj[key].deviceLogin) {
              devicesDetails.push(deviceLoginCommonInfoObj[key]);
            }
          }
        }
        setOtherDeviceDetails(devicesDetails);
      }

      setShowDetails(true);
      setLoading(false);
    } catch (err) {
      setErr(true);
      setDeviceCommonInfoObj();
      setShowDetails(false);
      setLoading(false);
    }
  }, [currentDeviceKey, userCognitoData]);

  /** Sequence-1: UseEffect to set get details for getUserProfileDetails **/
  useEffect(() => {
    if (userCognitoData) {
      getUserProfileDetails();
    }
  }, [getUserProfileDetails, userCognitoData]);

  /**Formatter function for time to display */
  const getFormattedTime = useCallback((time) => {
    return time ? formatDate(time, false, false) : "";
  }, []);

  /**param: data  ---> {type: String , deviceId: String }
   * data is of object type having 2 values:
   * type: "logoutAll" or "singleLogout", when called from LogoutFromAllDevice then pass "logoutAll"
   *        & when called from single device logout then pass "singleLogout"
   * deviceId: device Id (for single logout). Don't pass it for LogoutFromAllDevice
   **/
  const onLogoutClick = useCallback(
    (data) => {
      setConfirmModal((prev) => ({
        ...prev,
        open: true,
        msg: t("logoutOptions.logoutFromDevice"),
        type: data.type,
        data: data.type === "singleLogout" ? { deviceId: data.deviceId } : {},
      }));
    },
    [t]
  );

  const onConfirmModalClose = useCallback(() => {
    //Reset on modal close
    setConfirmModal((prev) => ({
      ...prev,
      open: false,
      msg: "",
      type: "",
      data: {},
    }));
  }, []);

  const onConfirm = useCallback(
    async (type, data) => {
      setConfirmModal((prev) => ({
        ...prev,
        open: false,
        msg: "",
        type: "",
      }));
      setLoading(true);
      try {
        const deviceIdArr = [];
        if (type === "logoutAll") {
          const commomInfoKeys = Object.keys(deviceCommonInfoObj);
          commomInfoKeys.forEach((id) => {
            deviceIdArr.push(id.split("&")[0]);
          });
        } else {
          deviceIdArr.push(data.deviceId);
          if (currentDeviceKey === data.deviceId) {
          } else {
            setOtherDeviceDetails((prev) =>
              prev.filter((prevData) => prevData.DeviceID !== data.deviceId)
            );
          }
        }
        const value = {
          deviceLogin: false,
          forceLogout: true,
        };
        await updateCommonInfo(userCognitoData?.username, deviceIdArr, value);
      } catch (err) {}
      if (type === "singleLogout" && currentDeviceKey !== data.deviceId) {
        setLoading(false);
      }
    },
    [currentDeviceKey, deviceCommonInfoObj, userCognitoData?.username]
  );

  return (
    <div className="logout-options-container component">
      {/* Header Section Starts */}
      {showDetails && (
        <>
          <div className="header-section">
            <div className="heading-h2 logout-option-heading">
              {t("UserPref.navOptions.logoutOption")}
            </div>
            <div
              className="logout-all logout-text"
              onClick={() => onLogoutClick({ type: "logoutAll" })}
            >
              <div>{Icons.userPref.logoutOptions}</div>
              <div className="heading-h2 logoutFromAllDevicesText">
                {t("logoutOptions.logoutFromAllDevices")}
              </div>{" "}
            </div>
          </div>
          {/* Header Section Ends */}

          {/* Body Section Starts */}
          <div className="body-section">
            {/* Current Device Section Starts */}
            <div>
              <div className="title">
                <div className="heading-h3">
                  {t("logoutOptions.currentDevice")}
                </div>
              </div>
              <div className="device-detail-container">
                <div className="d-flex gap-2">
                  <div>{Icons.laptopDevice}</div>
                  <div>
                    <div className="heading-h4 pb5">
                      {currentDevicesDetails?.deviceDetails?.deviceModel ||
                        `${browserName}${
                          isMobile ? " Mobile" : ""
                        } Browser`}{" "}
                      {""}(
                      {currentDevicesDetails?.deviceDetails?.osType || osName})
                    </div>
                    <div className="text-h2 sub-text">
                      {t("logoutOptions.activeNow")}
                    </div>
                  </div>
                </div>
                <div
                  className="heading-h3 logout-text logoutFromCurrentDevice"
                  onClick={() => {
                    onLogoutClick({
                      type: "singleLogout",
                      deviceId: currentDevicesDetails?.DeviceID,
                    });
                  }}
                >
                  {t("logoutOptions.logout")}
                </div>
              </div>
            </div>
            {/* Current Device Section Ends */}
            {/* Other Device Section Starts */}
            {otherDeviceDetails?.length ? (
              <div className="other-devices">
                <div className="title">
                  <div className="heading-h3">
                    {t("logoutOptions.otherDevices")}
                  </div>
                </div>
                <div>
                  {otherDeviceDetails.map((details, index) => {
                    return (
                      <div
                        className="device-detail-container"
                        key={`${index + 1}`}
                      >
                        <div className="d-flex gap-2">
                          <div>
                            {details?.deviceDetails?.deviceType === "Web"
                              ? Icons.laptopDevice
                              : Icons.mobileDevice}
                          </div>
                          <div>
                            <div className="heading-h4 pb5">
                              {details.deviceDetails.deviceModel} (
                              {details.deviceDetails.osType})
                            </div>
                            <div className="text-h2 sub-text">
                              {t("logoutOptions.lastActiveOn")}{" "}
                              <span className="text-h2 lastLoginTime">
                                {getFormattedTime(details?.lastLoginTime)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div
                          className={`heading-h3 logout-text logoutFromOtherDevices${index}`}
                          onClick={() => {
                            onLogoutClick({
                              type: "singleLogout",
                              deviceId: details?.DeviceID,
                            });
                          }}
                        >
                          {t("logoutOptions.logout")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <></>
            )}
            {/* Other Device Section Ends */}
          </div>
        </>
      )}
      {/* Body Section Ends */}
      {!isLoading && err && (
        <>
          <div className="header-section">
            <div className="heading-h2">
              {t("UserPref.navOptions.logoutOption")}
            </div>
          </div>
          <div className="empty-screen-container">
            <EmptyScreen
              img={Icons.userPref.noData}
              small={true}
              title={t("UserPref.noDataTitle")}
              desc={t("UserPref.noDataDescription")}
            />
          </div>
        </>
      )}
      {isLoading && <LoadingSpinner />}
      {/* Comfirmation Modal */}
      <ModalComponent
        idName="schedule-confirm-modal"
        isOpen={confirmModal.open}
        onCloseModal={onConfirmModalClose}
        descriptionText={confirmModal.msg}
        needConfirmation={true}
        onConfirm={() => {
          onConfirm(confirmModal.type, confirmModal.data);
        }}
      />
    </div>
  );
}

export default LogoutOptions;
